import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTrashablePromise } from './withTrashable/TrashableContext';
import { apiRawFetch } from '../APIClient';
import {
  OBServiceTypes,
  SensorProfile,
  StatusPageObjects,
  StatusSection,
} from '../types';
import { isActive } from '../reducers/risk';
import { sessionSelector } from '../reducers/session';
import { useFetchSchemasOnMount } from '../routes/self-service/connected-accounts/useFetchSchemasOnMount';
import { credentialSelector } from '../reducers/credential';
import { Link } from 'react-router-dom';

export const useFetchStatusPageData = () => {
  const { registerPromise } = useTrashablePromise();
  useFetchSchemasOnMount();
  const credSchemas = useSelector(credentialSelector.getSchemas);
  const customerId = useSelector(sessionSelector.getCustomerID);
  const [siteSensorData, setSiteSensorData] = React.useState<StatusPageObjects>(
    [],
  );

  const [productsData, setProductsData] = React.useState<StatusPageObjects>([]);
  const [cspmData, setCspmData] = React.useState<StatusPageObjects>([]);
  const [agentData, setAgentData] = React.useState<StatusPageObjects>([]);
  const [statusData, setStatusData] = React.useState<StatusPageObjects>([]);

  const loading = React.useMemo(() => statusData.length === 0, [statusData]);

  /**
   * Effect to fetch site sensor profiles
   */
  React.useEffect(() => {
    const generateSensorsObject = (sensors: SensorProfile[]) => {
      const children: StatusPageObjects = [];
      sensors.forEach((s) => {
        const state = s.deployment?.state ?? 'not deployed';
        // Get the customer friendly name for cloud sensors from cred schema
        const deploymentType =
          credSchemas.find((cs) => cs.name === s.deployment_type)
            ?.display_name ?? s.deployment_type;
        children.push({
          title: `${s.shorthand} - ${deploymentType} - ${state}`,
          status: state === 'installed' ? 'success' : 'fail',
        });
      });
      return children;
    };

    const fetchSiteSensorProfiles = async () => {
      try {
        const data = await registerPromise(apiRawFetch.getSiteSensorProfiles());
        const sites = Object.keys(data.sites);
        if (sites.length === 0 && data.orphans.length === 0) {
          setSiteSensorData([
            {
              title: 'No sites and sensors were found',
            },
          ]);
        } else {
          const result: StatusPageObjects = [];
          sites.forEach((site) => {
            const sensors = data.sites[site];
            result.push({
              title: site,
              children: generateSensorsObject(sensors),
            });
          });
          if (data.orphans.length > 0) {
            result.push({
              title: 'No Site Assigned',
              children: generateSensorsObject(data.orphans),
            });
          }
          setSiteSensorData(result);
        }
      } catch (err) {
        setSiteSensorData([
          { title: "Failed to fetch site's sensor data", status: 'fail' },
        ]);
      }
    };
    fetchSiteSensorProfiles();
  }, [registerPromise, setSiteSensorData, credSchemas]);

  /**
   * Effect to fetch AW agents
   */
  React.useEffect(() => {
    const fetchAgent = async () => {
      try {
        const result = await registerPromise(apiRawFetch.getAgents());
        const agents = result['data'];
        const onlineAgents = agents.filter((agent) =>
          isActive(agent.heartbeat.createTime),
        );

        if (onlineAgents.length === 0) {
          setAgentData([
            {
              title: 'No online agents were found',
            },
          ]);
          return;
        }

        const statusSection: StatusSection = {
          title: onlineAgents
            .map((agent) => {
              return agent.hostDetail.hostname;
            })
            .join(', '),
        };

        const isPlural = onlineAgents.length > 1;

        setAgentData([
          {
            title: `${onlineAgents.length} AW ${
              onlineAgents.length > 1 ? 'agents are' : 'agent is'
            } online`,
            titleNode: (
              <span>
                {onlineAgents.length} AW{' '}
                <Link to="/dashboard/endpoints">
                  {isPlural ? 'Agents' : 'Agent'}
                </Link>{' '}
                {isPlural ? 'are' : 'is'} online
              </span>
            ),
            status: onlineAgents.length > 0 ? 'success' : 'fail',
            children: [statusSection],
          },
        ]);
      } catch (err) {
        setAgentData([{ title: 'Failed to fetch agent data', status: 'fail' }]);
      }
    };
    fetchAgent();
  }, [registerPromise, setAgentData]);

  /**
   * Effect to pull salesforce subscriptions
   */
  React.useEffect(() => {
    const fetchSFSubscriptions = async () => {
      try {
        const result = await registerPromise(
          apiRawFetch.getSalesforceSubscriptions(customerId),
        );
        const products: StatusPageObjects = [];
        let hasLogSearch = false;
        let hasMA = false;
        const cspm: StatusPageObjects = [];
        if (result.products) {
          result.products.forEach((product) => {
            if (product.serviceType === OBServiceTypes.logSearch) {
              hasLogSearch = true;
            }
            if (
              product.serviceType === OBServiceTypes.managedAwareness ||
              product.serviceType === OBServiceTypes.managedAwarenessPlus
            ) {
              hasMA = true;
            }
            if (product.serviceType && product.serviceType.includes('cspm')) {
              cspm.push({
                title: product.serviceType,
              });
            }
          });
        }
        if (hasLogSearch) {
          products.push({
            title: 'Log Search',
          });
        }
        if (hasMA) {
          products.push({
            title: 'Managed Awareness',
          });
        }
        if (products.length > 0) {
          setProductsData([
            {
              title: 'Purchased Products',
              children: products,
            },
          ]);
        }

        if (cspm.length > 0) {
          setCspmData([
            {
              title: 'CSPM Add-ons',
              children: cspm,
            },
          ]);
        }
      } catch (e) {
        setProductsData([
          {
            title: 'Failed to fetch purchased products and add-ons',
            status: 'fail',
          },
        ]);
      }
    };
    fetchSFSubscriptions();
  }, [customerId, registerPromise]);

  /**
   * Effect to combine the product and site sensor data
   */
  React.useEffect(() => {
    // siteSensorData will always contain one entry if the site-sensor-profile api was called.
    setStatusData([
      ...productsData,
      ...cspmData,
      ...siteSensorData,
      ...agentData,
    ]);
  }, [siteSensorData, productsData, cspmData, agentData, setStatusData]);

  return { loading, statusData };
};
